import { useMemo, useState } from "react";
import { useForm, useWatch, FormProvider } from "react-hook-form";
import { TrashIcon, PlusIcon, PencilIcon } from "lucide-react";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
} from "@babackapp/ui/components/form";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@babackapp/ui/components/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Badge } from "@babackapp/ui/components/badge";
import { Button } from "@babackapp/ui/components/button";
import { ButtonLoading } from "@babackapp/ui/components/button";
import FormInput from "@babackapp/react-hook-forms-components/form-input";
import { Separator } from "@babackapp/ui/components/separator";
import { Switch } from "@babackapp/ui/components/switch";
import useTranslation from "@babackapp/translations/use-translation";
import useUpdatePortalBasics from "@babackapp/private-api-client/client/use-update-portal-basics";
import { useCurrentShopSuspense } from "@/api/shops/use-current-shop-suspense";
import { RuleConditionsBuilder } from "@/components/override-rules/rule-conditions-builder";
import { createDefaultConditions, OPERATORS } from "@/lib/override-rules";
import type { FieldIdDefinition } from "@/lib/override-rules";
import useShopifySegments from "@babackapp/private-api-client/client/use-shopify-segments";
import { useLDFlags } from "@/hooks/use-ld-flags";

const BASE_SEGMENT_EXCLUDE_FIELDS: FieldIdDefinition[] = [
  "orderTotalAmount",
  "orderTags",
  "orderCreatedAt",
  "orderItemCount",
  "productTags",
  "returnReasonIds",
  "returnCreatedAt",
  "returnItemCount",
  "returnItemResolutions",
  "customerShippingCountryCode",
  "customerShippingCity",
];

type Segment = {
  name: string;
  conditions?: {
    rules: { id: string; type: string; operator: string; value: unknown }[];
    operator: "AND" | "OR";
  } | null;
  hasFreeReturn: boolean;
};

type SegmentsFormValues = {
  customerSegments: Segment[];
};

function ConditionsSummary({ segment }: { segment: Segment }) {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { data: shopifySegments } = useShopifySegments(shop?.id);
  const rules = (segment.conditions?.rules ?? []).filter((r) => r.id);

  if (rules.length === 0) {
    return (
      <span className="text-xs text-muted-foreground italic">
        {t("settings.config.customerSegments.summary.noConditions")}
      </span>
    );
  }

  const logicalOp =
    segment.conditions?.operator === "OR" ? t("misc.or") : t("misc.and");

  return (
    <div className="flex flex-wrap items-center gap-1">
      {rules.map((rule, ruleIndex) => (
        <span key={ruleIndex} className="flex items-center gap-1">
          {ruleIndex > 0 && (
            <span className="text-xs text-muted-foreground font-medium">
              {logicalOp}
            </span>
          )}
          <Badge variant="outline" className="text-xs font-normal">
            {t(`overrideRules.fields.${rule.id}.label`)}
            {rule.operator && (
              <span className="text-muted-foreground mx-0.5">
                {t(`overrideRules.operators.${rule.operator}.label`)}
              </span>
            )}
            {rule.value != null && (
              <span className="font-medium">
                {formatRuleValue(rule.value, rule.id, shopifySegments)}
              </span>
            )}
          </Badge>
        </span>
      ))}
    </div>
  );
}

function formatRuleValue(
  value: unknown,
  fieldId: string,
  shopifySegments?: { id: string; name: string }[],
): string {
  if (
    fieldId === "customerExternalSegments" &&
    typeof value === "string" &&
    shopifySegments
  ) {
    return shopifySegments.find((s) => s.id === value)?.name ?? String(value);
  }
  if (typeof value === "object" && value !== null && "key" in value) {
    const kv = value as { key: string; value: string };
    return `${kv.key}=${kv.value}`;
  }
  if (value && Array.isArray(value)) {
    return value.join(", ");
  }
  return String(value);
}

function SegmentRow({
  segment,
  onEdit,
  onRemove,
}: {
  segment: Segment;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const t = useTranslation();

  return (
    <div className="border rounded-lg p-4 space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <span className="font-medium truncate">{segment.name || "—"}</span>
          {segment.hasFreeReturn && (
            <Badge variant="secondary" className="text-xs shrink-0">
              {t("settings.config.customerSegments.inputs.hasFreeReturn.label")}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-7"
            onClick={onEdit}
          >
            <PencilIcon className="size-3.5" />
          </Button>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-muted-foreground hover:text-destructive"
              >
                <TrashIcon className="size-3.5" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t("settings.config.customerSegments.deleteConfirm.title")}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t(
                    "settings.config.customerSegments.deleteConfirm.description",
                    { name: segment.name },
                  )}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t("misc.cancel")}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={onRemove}
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                >
                  {t("misc.delete")}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
      <ConditionsSummary segment={segment} />
    </div>
  );
}

function ensureRuleOperators(segment: Segment): Segment {
  if (!segment.conditions?.rules) return segment;
  return {
    ...segment,
    conditions: {
      ...segment.conditions,
      rules: segment.conditions.rules.map((rule) => {
        if (rule.operator) return rule;
        const defaultOp = OPERATORS.find((op) =>
          op.supportedTypes.includes(rule.type as never),
        )?.value;
        return { ...rule, operator: defaultOp ?? rule.operator };
      }),
    },
  };
}

function SegmentEditDialog({
  segment,
  open,
  onClose,
  onSave,
  isPending,
  excludeFields,
}: {
  segment: Segment;
  open: boolean;
  onClose: () => void;
  onSave: (segment: Segment) => void;
  isPending: boolean;
  excludeFields: FieldIdDefinition[];
}) {
  const t = useTranslation();
  const form = useForm<Segment>({
    defaultValues: ensureRuleOperators(segment),
  });

  const name = useWatch({ control: form.control, name: "name" });

  const handleSave = form.handleSubmit((data) => {
    onSave(data);
  });

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {name || t("settings.config.customerSegments.inputs.addSegment")}
          </DialogTitle>
        </DialogHeader>

        <FormProvider {...form}>
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormInput
                  label={t(
                    "settings.config.customerSegments.inputs.name.label",
                  )}
                  {...field}
                />
              )}
            />

            <Separator />

            <FormField
              control={form.control}
              name="conditions"
              render={({ field }) => {
                if (!field.value) {
                  return (
                    <FormItem>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          field.onChange(createDefaultConditions())
                        }
                      >
                        <PlusIcon className="size-4" />
                        {t(
                          "settings.config.customerSegments.inputs.addConditions",
                        )}
                      </Button>
                    </FormItem>
                  );
                }
                return (
                  <FormItem className="space-y-2">
                    <div className="flex items-center justify-between">
                      <FormLabel>
                        {t(
                          "settings.config.customerSegments.inputs.conditions.label",
                        )}
                      </FormLabel>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => field.onChange(undefined)}
                      >
                        <TrashIcon className="size-4" />
                      </Button>
                    </div>
                    <RuleConditionsBuilder
                      value={field.value}
                      onChange={field.onChange}
                      excludeFields={excludeFields}
                    />
                  </FormItem>
                );
              }}
            />

            <Separator />

            <FormField
              control={form.control}
              name="hasFreeReturn"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between">
                  <FormLabel>
                    {t(
                      "settings.config.customerSegments.inputs.hasFreeReturn.label",
                    )}
                  </FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />
          </div>
        </FormProvider>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            {t("misc.cancel")}
          </Button>
          <ButtonLoading
            type="button"
            isLoading={isPending}
            onClick={handleSave}
          >
            {t("misc.save")}
          </ButtonLoading>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const CustomerSegmentsForm = () => {
  const t = useTranslation();
  const { data: shop } = useCurrentShopSuspense();
  const { mutateAsync: updatePreferences, isPending } =
    useUpdatePortalBasics(shop);
  const { hasCustomerReturnRateSegment } = useLDFlags({ shopId: shop.id });

  const [segments, setSegments] = useState<Segment[]>(
    () => shop.preferences.customerSegments ?? [],
  );
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const excludeFields = useMemo<FieldIdDefinition[]>(() => {
    const fields = [...BASE_SEGMENT_EXCLUDE_FIELDS];
    if (!hasCustomerReturnRateSegment) {
      fields.push("customerReturnRateLast12Months");
    }
    return fields;
  }, [hasCustomerReturnRateSegment]);

  const saveSegments = async (updatedSegments: Segment[]) => {
    await updatePreferences({ customerSegments: updatedSegments });
    setSegments(updatedSegments);
  };

  const handleSaveSegment = async (index: number, segment: Segment) => {
    const updated = [...segments];
    updated[index] = segment;
    await saveSegments(updated);
    setEditingIndex(null);
  };

  const removeSegment = async (index: number) => {
    const updated = segments.filter((_, i) => i !== index);
    await saveSegments(updated);
  };

  const addSegment = () => {
    const newSegment: Segment = {
      name: "",
      conditions: undefined,
      hasFreeReturn: false,
    };
    setSegments([...segments, newSegment]);
    setEditingIndex(segments.length);
  };

  return (
    <div className="space-y-4">
      <FormDescription>
        {t("settings.config.customerSegments.description")}
      </FormDescription>
      <div className="space-y-2">
        {segments.map((segment, index) => (
          <SegmentRow
            key={index}
            segment={segment}
            onEdit={() => setEditingIndex(index)}
            onRemove={() => removeSegment(index)}
          />
        ))}
      </div>
      {editingIndex !== null && segments[editingIndex] && (
        <SegmentEditDialog
          key={editingIndex}
          segment={segments[editingIndex]}
          open
          onClose={() => setEditingIndex(null)}
          onSave={(segment) => handleSaveSegment(editingIndex, segment)}
          isPending={isPending}
          excludeFields={excludeFields}
        />
      )}
      <Button
        type="button"
        className="flex gap-2 justify-self-start"
        size="sm"
        variant="outline"
        onClick={addSegment}
      >
        {t("settings.config.customerSegments.inputs.addSegment")}
        <PlusIcon className="size-4" />
      </Button>
    </div>
  );
};
